import { Box, Button, TextField } from '@mui/material';
import { NormalText, SmallText } from 'components/atoms/Typography';
import useTranslation from 'next-translate/useTranslation';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/reducers';
import { useCartService } from 'services/cart/useCartService';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface ICouponProps {}

const Coupon = ({}: ICouponProps) => {
    const cartService = useCartService();
    const [coupon, setCoupon] = useState<{ couponCode: string; couponId: string }>(null);
    const userCart = useSelector((state: RootState) => state.userCart).data;

    const [invalidCoupon, setInvalidCoupon] = useState<boolean>(false);
    const [invalidCouponMessage, setInvalidCouponMessage] = useState<string>('');
    const [isCouponValid, setIsCouponValid] = useState<boolean>(false);

    const { t } = useTranslation('common');

    useEffect(() => {
        if (userCart?.coupon) {
            setCoupon(userCart.coupon);
        } else {
            setIsCouponValid(false);
            setCoupon({ couponCode: '', couponId: '' });
        }
    }, [userCart?.coupon]);

    const handleCouponInput = event => {
        setCoupon({ ...coupon, couponCode: event.target.value });
        if (invalidCoupon) {
            setInvalidCouponMessage('');
            setInvalidCoupon(false);
        }
    };

    const handleCouponVerify = async () => {
        const res = await cartService.addCoupon(coupon);
        if (!res.success) {
            setIsCouponValid(false);
            setInvalidCoupon(true);
            setInvalidCouponMessage(res.data?.details?.message || t('invalidCoupon'));
        } else {
            if (coupon) {
                setIsCouponValid(true);
            } else {
                setIsCouponValid(false);
            }
            setInvalidCouponMessage('');
            setInvalidCoupon(false);
        }
    };

    const handleRemoveCoupon = async () => {
        setIsCouponValid(false);
        setInvalidCouponMessage('');
        await cartService
            .addCoupon({ couponCode: userCart?.coupon?.couponCode, couponId: userCart?.coupon?.couponId })
            .then(() => {
                setCoupon({ couponCode: '', couponId: '' });
            });
    };

    return (
        <Box my={4}>
            {/* <NormalText semibold>{t('coupon', { count: 1 })}</NormalText> */}
            {invalidCoupon && (
                <Box>
                    <SmallText color="primary">{invalidCouponMessage}</SmallText>
                </Box>
            )}
            <Box mt={1} display="flex">
                <Box position="relative" width="100%">
                    <TextField
                        placeholder={t('insertCoupon')}
                        sx={{ borderRadius: '0px', '& .MuiOutlinedInput-input': { height: 3 } }}
                        fullWidth={true}
                        value={coupon?.couponCode}
                        onChange={handleCouponInput}
                    />
                    {isCouponValid && (
                        <CheckCircleIcon
                            fontSize="large"
                            sx={{ color: 'green', position: 'absolute', zIndex: 1, top: '10px', right: '10px' }}
                        />
                    )}
                </Box>
                <Button
                    variant="contained"
                    sx={{
                        color: 'white',
                        ml: -0.5,
                        ':disabled': {
                            backgroundColor: 'lightgray',
                        },
                    }}
                    disabled={coupon?.couponId !== ''}
                    onClick={handleCouponVerify}
                >
                    {t('apply')}
                </Button>
            </Box>
            {coupon?.couponId && (
                <Box position="relative">
                    <Button
                        variant="text"
                        color="primary"
                        sx={{ marginTop: '2px', position: 'absolute', right: '0' }}
                        onClick={handleRemoveCoupon}
                    >
                        {t('remove')}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default Coupon;
